import React from 'react';

import AddLayerButton from 'samples/plate-maps/display-layer-controls/AddLayerButton';
import SearchablePlateProcedureParameterDropdown from 'samples/plate-maps/display-layer-controls/SearchablePlateProcedureParameterDropdown';
import {PlateDataParameter, PlateMaterialInput} from 'samples/plate-maps/PlateMapConstants';

export interface PlateProcedureParameterLayerSelectorProps<
  TParameter extends PlateDataParameter | PlateMaterialInput,
> {
  /** A list of plate-used procedure parameters to search through & select */
  parameters: Array<TParameter>;
  /** The maximum amount of parameters that may be selected at one time */
  maxSelectedValues?: number;
  /** A function to be called when a parameter is selected or deselected */
  onSelect: (selectedValues: Array<string>) => void;
  /** The list of versionId of parameters that are currently selected */
  selectedValues: Array<string>;
  /** Whether the dropdown is open */
  isOpen: boolean;
  /** A function to set whether the dropdown is open */
  setIsOpen: (isOpen: boolean) => void;
}

export default function PlateProcedureParameterLayerSelector<
  TParameter extends PlateDataParameter | PlateMaterialInput,
>({
  parameters,
  maxSelectedValues,
  onSelect,
  selectedValues,
  isOpen,
  setIsOpen,
}: PlateProcedureParameterLayerSelectorProps<TParameter>): JSX.Element {
  if (parameters.length === 0) {
    return <></>;
  }

  const tooManySelected = maxSelectedValues != null && selectedValues.length >= maxSelectedValues;
  return (
    <SearchablePlateProcedureParameterDropdown<TParameter>
      parameters={parameters}
      disabled={tooManySelected}
      closeOnInsideClick={false}
      dropdownToggleOverride={
        <AddLayerButton
          disabled={tooManySelected}
          onClick={() => {
            if (!tooManySelected) {
              setIsOpen(!isOpen);
            }
          }}
        />
      }
      onSelect={(newSelectedValues: Array<string>) => {
        onSelect(newSelectedValues);
        if (maxSelectedValues != null && newSelectedValues.length >= maxSelectedValues) {
          setIsOpen(false);
        }
      }}
      selectedValues={selectedValues}
      getOptionData={(parameter) => ({
        name: parameter.name,
        value: parameter.versionId,
        icon: 'check' as const,
        disabled: tooManySelected && !selectedValues.includes(parameter.versionId),
      })}
      isOpen={isOpen}
      onToggle={(newIsOpen) => {
        if (!tooManySelected) {
          setIsOpen(newIsOpen);
        } else if (isOpen) {
          setIsOpen(false);
        }
      }}
      noneSelectedMessage=""
    />
  );
}
